/**
 * FIAS auto-select logic — mirrors dashboard's fiasTab/fiasBlur helpers
 * (backend/src/main/kotlin/ru/autoquest/routes/DashboardRoutes.kt:674-716).
 *
 * Rules (same as dashboard, no disabled-field cascade):
 *   1. If an objectId is already selected for this field → no-op (user confirmed).
 *   2. If input is empty → no-op.
 *   3. If dropdown has no items → no-op.
 *   4. If exactly one item whose name matches input case-insensitively → select it.
 *   5. If no exact match but exactly one item available → select it.
 *   6. Otherwise → no-op (user still needs to pick).
 *
 * This module is pure (no DOM) so it can be unit-tested directly.
 * The view wires it to blur + Tab keydown events on city/street/house inputs.
 */

export interface FiasItem {
  objectId: number;
  name: string;
}

export interface AutoSelectResult {
  /** The chosen item, or null if nothing should be auto-selected. */
  selected: FiasItem | null;
  /** Reason code, for tests and debugging. */
  reason:
    | 'already-selected'
    | 'empty-query'
    | 'no-items'
    | 'exact-match'
    | 'single-item'
    | 'no-match';
}

/**
 * Decide whether to auto-select an item given the current input value
 * and the items currently rendered in the dropdown.
 *
 * @param query         Raw input value (will be trimmed).
 * @param items         Items currently shown in the suggestion dropdown.
 * @param alreadySelected  True if the hidden objectId field already holds
 *                         a confirmed selection for this input.
 */
// Regex matching Russian address type prefixes (mirrors backend addressTypePrefixRegex).
// Used to strip "ул.", "пр-кт.", "г.", "улица", etc. before comparing names.
const typePrefixRe = /^(?:пр-кт\.?|пр-д\.?|ул\.?|улица|пер\.?|переулок|б-р\.?|бульвар|наб\.?|набережная|пл\.?|площадь|ш\.?|шоссе|пр\.?|проспект|проезд|г\.?|город|с\.?|д\.?|пос\.?|пгт\.?|п\.|село|деревня|поселок|посёлок|тер\.?|территория|мкр\.?|микрорайон|ал\.?|аллея|кв-л|квартал|тупик|туп\.?|снт|ст|днт|гск|тсн|днп|снп|с\/т|с\/о|ж\/м)[.\s]\s*/i;

function stripTypePrefix(s: string): string {
  let r = s;
  let prev: string;
  do { prev = r; r = r.replace(typePrefixRe, ''); } while (r !== prev);
  return r.trim();
}

export function tryAutoSelect(
  query: string,
  items: readonly FiasItem[],
  alreadySelected: boolean,
): AutoSelectResult {
  if (alreadySelected) return { selected: null, reason: 'already-selected' };

  const q = query.trim();
  if (!q) return { selected: null, reason: 'empty-query' };

  if (items.length === 0) return { selected: null, reason: 'no-items' };

  // Exact, case-insensitive name match — prefer this even when there are many items.
  const lowered = q.toLowerCase();
  const exact = items.find((it) => it.name.toLowerCase() === lowered);
  if (exact) return { selected: exact, reason: 'exact-match' };

  // Match after stripping type prefix: "Ульянова" matches "ул. Ульянова".
  // Select the first item whose bare name matches the query.
  const qBare = stripTypePrefix(lowered);
  if (qBare) {
    const normalized = items.find((it) => stripTypePrefix(it.name.toLowerCase()) === qBare);
    if (normalized) return { selected: normalized, reason: 'exact-match' };
  }

  // No exact match: auto-select only when the dropdown narrowed to a single option.
  if (items.length === 1) return { selected: items[0], reason: 'single-item' };

  return { selected: null, reason: 'no-match' };
}

/**
 * Circular index step for keyboard navigation in a dropdown list.
 *
 * - `direction: 1`  → next (ArrowDown), wraps last → 0
 * - `direction: -1` → prev (ArrowUp),   wraps 0 → last
 * - empty list → -1 (caller should ignore the event)
 *
 * Used by both the player PWA (`location-view.ts`) and the organizer dashboard
 * inline JS (`DashboardRoutes.kt`) for the FIAS suggestion dropdown.
 */
export function nextIndex(current: number, length: number, direction: 1 | -1): number {
  if (length <= 0) return -1;
  // "No highlight" → land on the sensible edge so the user sees something.
  if (current < 0) return direction > 0 ? 0 : length - 1;
  // Double-mod trick makes negative results wrap correctly: ((-1 % 3) + 3) % 3 === 2
  return (((current + direction) % length) + length) % length;
}

